import React from 'react';
import { Navigate, Route } from 'react-router';

import { createApp } from '@backstage/app-defaults';
import { FlatRoutes } from '@backstage/core-app-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import {
  IdentityApi,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  catalogPlugin,
  CatalogEntityPage,
  CatalogIndexPage,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  catalogImportPlugin,
  CatalogImportPage,
} from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  scaffolderPlugin,
  ScaffolderPage,
  ScaffolderFieldExtensions,
} from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import {
  techdocsPlugin,
  TechDocsIndexPage,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechRadarPage } from '@backstage/plugin-tech-radar';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { EntitySnykContent } from 'backstage-plugin-snyk';

import { apis } from './apis';
import { providers } from './identityProviders';
import * as plugins from './plugins';

import { Root } from './components/Root';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { KebabCaseTextFieldExtension } from './components/scaffolder';

// Custom Pages
import {
  AwsDashboardPage,
  AwsDashboardPendingAccountPage,
} from 'plugin-aws-dashboard';
import { EngagementTrackerPage } from 'plugin-engagement-tracker';
import { RouterVisualizerPage } from 'plugin-router-visualizer';
import { ApfPage } from 'plugin-apf';

import { KingpinUiPage as FdmPage } from '@apptio-pne/backstage-plugin-fdm';

// Parses supplied JWT token and returns the payload
function parseJwt(token: string): { exp: number } {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

// Returns milliseconds until the supplied JWT token expires
function msUntilExpiry(token: string): number {
  const payload = parseJwt(token);
  const remaining =
    new Date(payload.exp * 1000).getTime() - new Date().getTime();
  return remaining;
}

// Calls the specified url regularly using an auth token to set a token cookie
// to authorize regular HTTP requests when loading techdocs
async function setTokenCookie(url: string, identityApi: IdentityApi) {
  const { token } = await identityApi.getCredentials();
  if (!token) {
    return;
  }

  await fetch(url, {
    mode: 'cors',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // Call this function again a few minutes before the token expires
  const ms = msUntilExpiry(token) - 4 * 60 * 1000;
  setTimeout(
    () => {
      setTokenCookie(url, identityApi);
    },
    ms > 0 ? ms : 10000,
  );
}

const app = createApp({
  apis,
  plugins: Object.values(plugins),
  components: {
    SignInPage: props => {
      const discoveryApi = useApi(discoveryApiRef);

      async function onSignInSuccess(identityApi: IdentityApi) {
        setTokenCookie(await discoveryApi.getBaseUrl('cookie'), identityApi);

        props.onSignInSuccess(identityApi);
      }

      if (providers.length == 1) {
        return (
          <SignInPage
            {...props}
            provider={providers[0] as any} // Messed up type
            auto={true}
            title="Sign In"
            align="center"
            onSignInSuccess={onSignInSuccess}
          />
        );
      } else {
        return (
          <SignInPage
            {...props}
            providers={providers}
            title="Sign In"
            align="center"
            onSignInSuccess={onSignInSuccess}
          />
        );
      }
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: scaffolderPlugin.routes.root,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const AppProvider = app.getProvider();
const AppRouter = app.getRouter();

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={<CatalogIndexPage initiallySelectedFilter="all" />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={[
            {
              title: 'Catalog Utilities',
              filter: entity => entity?.spec?.type === 'catalog',
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <KebabCaseTextFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={
        <TechRadarPage
          width={1800}
          height={1150}
          title="Apptio Tech Radar"
          subtitle="Pick the recommended technologies for your projects (v1.1, August 2021)"
          pageTitle="Tech Radar"
        />
      }
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/snyk" element={<EntitySnykContent />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    /* Custom Pages */
    <Route path="/aws-dashboard" element={<AwsDashboardPage />} />
    <Route
      path="/aws-dashboard/pending-accounts/:id"
      element={<AwsDashboardPendingAccountPage />}
    />
    <Route path="/engagement" element={<EngagementTrackerPage />} />
    <Route path="/kingpin-ui" element={<FdmPage />} />
    <Route path="/router-visualizer" element={<RouterVisualizerPage />} />
    <Route path="/apf" element={<ApfPage />} />
  </FlatRoutes>
);

const App = () => (
  <AppProvider>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </AppProvider>
);

export default App;
